import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { FormFeedback } from '@/components/ui/form-feedback';
import { TableSkeleton } from '@/components/ui/skeleton';
import { apiClient } from '@/lib/api-client';
import { todayLocalDate } from '@/lib/intl';
import { AtRiskVisitsTable, type AtRiskVisitEntry } from './AtRiskVisitsTable';

type LoadState =
  | { status: 'loading' }
  | { status: 'forbidden' }
  | { status: 'error' }
  | { status: 'ready'; entries: AtRiskVisitEntry[] };

/**
 * The compliance safety net (FR-9.21, Resolution R2), composed PROMINENTLY at the top of
 * `ClinicalStudyDetailPage`'s composed-sections block. Reuses `clinical-study:appointment:manage`
 * — the SAME operational permission `StudySubjectsSection`'s own writes already require.
 * Reachability (verified, Resolution R2): this whole page renders only after a
 * `clinical-study:study:manage`-gated study fetch succeeds, so a SINGLE-role study administrator
 * reaches the page but 403-degrades this at-risk section (lacks `appointment:manage`), while a
 * SINGLE-role operator holds `appointment:manage` but cannot reach the page at all (lacks
 * `study:manage`). Per the Human decision of 2026-07-27 (run-journal receipt DR-0043-A), MVP
 * operator users are provisioned with BOTH the operational and administrator roles, so a real
 * user reaches the page AND sees this data; the single-role split is a documented known
 * constraint to revisit post-MVP. `asOfDate` is always `todayLocalDate()` — never
 * operator-editable (Resolution R5).
 */
export function AtRiskVisitsSection({
  studyId,
  onDrillDown,
}: {
  studyId: string;
  onDrillDown: (subjectId: string) => void;
}) {
  const { t } = useTranslation('clinical-study');
  const [state, setState] = useState<LoadState>({ status: 'loading' });

  const load = () => {
    setState({ status: 'loading' });
    apiClient
      .GET('/v1/clinical-study/studies/{studyId}/at-risk-visits', {
        params: { path: { studyId }, query: { asOfDate: todayLocalDate() } },
      })
      .then(({ data, error, response }) => {
        if (response.status === 403) {
          setState({ status: 'forbidden' });
          return;
        }
        if (error !== undefined || !response.ok || data === undefined) {
          setState({ status: 'error' });
          return;
        }
        setState({ status: 'ready', entries: data });
      })
      .catch(() => setState({ status: 'error' }));
  };

  useEffect(load, [studyId]);

  return (
    <Card>
      <CardHeader>
        <CardTitle>{t('atRisk.title')}</CardTitle>
      </CardHeader>
      <CardContent className="space-y-4">
        {state.status === 'loading' && <TableSkeleton columns={6} />}
        {state.status === 'forbidden' && (
          <FormFeedback tone="error">{t('atRisk.forbiddenMessage')}</FormFeedback>
        )}
        {state.status === 'error' && (
          <div className="space-y-2">
            <FormFeedback tone="error">{t('atRisk.loadError')}</FormFeedback>
            <Button type="button" variant="outline" size="sm" onClick={load}>
              {t('atRisk.retry')}
            </Button>
          </div>
        )}
        {state.status === 'ready' && (
          <AtRiskVisitsTable entries={state.entries} onDrillDown={onDrillDown} />
        )}
      </CardContent>
    </Card>
  );
}
